import find from 'lodash/find';
import Ncs from 'ncs';
import isArray from 'lodash/isArray'
import map from 'lodash/map'
import isNil from 'lodash/isNil'

const coreflOrders = [
  {name: 'filename', criteria: [{field: 'ncs_documents.filename', asc: true}]},
  {name: 'medium', criteria: [{field: 'ncs_documents.medium', asc: true}]},
  {name: 'task_title', criteria: [{field: 'ncs_documents.task_title', asc: true}]},
  {name: 'sex', criteria: [{field: 'ncs_documents.sex', asc: true}]},
  {name: 'age', criteria: [{field: 'ncs_documents.age', asc: true}]},
  {name: 'native_language', criteria: [{field: 'ncs_documents.native_language', asc: true}]},
  {name: 'proficiency_level', criteria: [{field: 'ncs_documents.proficiency_level', asc: true}]},
  {
    name: 'placement_test_percentage_score',
    criteria: [{field: 'ncs_documents.placement_test_percentage_score', asc: true}]
  },
  {
    name: 'proficiency_level', criteria: [{field: 'ncs_documents.proficiency_level', asc: true}],
    valid: q => find(q.filters, {name: 'ncs_documents.subcorpus', value: 'Learners'})
  },
]

function rangeGroupConfig(field) {
  return {
    [`ncs_documents.${field}_range`]: {
      filters: [`ncs_documents.${field}_from`, `ncs_documents.${field}_to`],
      getValues: v => v.split('-'),
      sortBy: v => !v ? null : parseInt(v.split('-').pop())
    }
  }
}

function orderOptionsAlphabetically({label, option}) {
  return label
}

export default {
  corefl: {
    fields: {
      Learners: ['filename', 'native_language', 'age', 'placement_test_raw_score', 'placement_test_percentage_score', 'proficiency_level', 'proficiency_self_assessment_fraction', 'age_studying_was_started', 'years_studying', 'stay_abroad_months', 'task_title', 'medium'],
      Native: ['filename', 'age', 'variety_of_native_language', 'foreign_language', 'proficiency_self_assessment_fraction', 'task_title', 'medium'],
    },
    // Navigation result has only a subset of the custom fields.
    navigation: {
      Learners: ['filename', 'native_language', 'age', 'placement_test_percentage_score', 'proficiency_level', 'proficiency_self_assessment', 'age_studying_was_started', 'years_studying', 'stay_abroad_months', 'task_title', 'medium'],
      Native: ['filename', 'age', 'variety_of_native_language', 'foreign_language', 'proficiency_self_assessment', 'task_title', 'medium'],
    },
    groups: {
      Learners: ['sex', 'medium', 'native_language', 'native_language_medium', 'proficiency_level', 'task_title', 'years_studying_range', 'stay_abroad_months_range', 'age_studying_was_started_range', 'age', 'placement_test_percentage_score_int_range'],
      Native: ['sex', 'medium', 'native_language', 'native_language_medium', 'task_title', 'age']
    }
  },
  fieldValue(value) {
    if (isArray(value)) {
      return value.length === 0 ? '' : map(value, v => this.fieldValue(v)).join(', ')
    } else if (value === true) {
      return 'Yes'
    } else if (value === false) {
      return 'No'
    } else if (isNil(value)){
      return ''
    } else {
      return value
    }
  },
  elements: {
    types: ['units', 'tokens', 'texts']
  },
  filters: {
    values: gon.values,
    dependencyValues: gon.dependencyValues
  },
  result: {
    error: {
      contactLink: './contacto',
      contactEmail: 'support@learnercorpora.com'
    },
    types: ['kwic', 'navigation', 'frequency', 'full_frequency'],
    detailTypes: ['context', 'task'],
    defaultType: 'kwic',
    kwic: {
      contextOnRowClick: true,
      order: {
        default: ['matching', 'previous_1'],
        orders: [...Ncs.utils.defaultOrder(5), ...coreflOrders],
        order: orderOptionsAlphabetically
      }
    },
    full_frequency: {
      groups: {
        ...rangeGroupConfig('age'),
        ...rangeGroupConfig('years_studying'),
        ...rangeGroupConfig('placement_test_percentage_score_int'),
        ...rangeGroupConfig('age_studying_was_started'),
        ...rangeGroupConfig('stay_abroad_months'),
        'ncs_documents.native_language_medium': {
          // When clicking one link of the combined table, set l1 for both native and learners filter.
          // They refer to the same field (l1) but they are different elements in the interface.
          filters: ['ncs_documents.l1-learners', 'ncs_documents.l1-native', 'ncs_documents.medium'],
          getValues: v => {
            const [native_language, medium] = v.split(' ')
            return [[native_language], [native_language], medium]
          },
          sortBy: v => {
            const [native_language, medium] = v.split(' ')
            return [native_language, medium === 'WR' ? 0 : 1] // Written should be first
          }
        },
        'ncs_documents.task_title': {
          sortBy: v => parseInt(v.split('. ')[0]) // Sort by task number
        },
        'ncs_documents.native_language': {
          filters: ['ncs_documents.l1-learners', 'ncs_documents.l1-native'],
          getValues: v => [[v], [v]]
        }
      }
    },
    navigation: {
      endpoint: 'navigation',
      defaultDisplay: 'navigation',
      elements: {
        types: ['texts'],
        defaultType: 'texts',
        // Required due to the ood behaviour of simple search form
        // Show navigation if no words are written. Kwic otherwise.
        // But to show words input this value has to be defined for navigation.
        maximum: 5
      },
      distances: false,
      order: {
        orders: coreflOrders,
        default: ['native_language', 'proficiency_level', 'placement_test_percentage_score', 'age', 'filename'],
        order: orderOptionsAlphabetically
      },
      pagination: true
    },
    task: {
      endpoint: 'task',
      elements: {
        types: ['texts'],
        defaultType: 'texts',
        // Required due to the ood behaviour of simple search form
        // Show navigation if no words are written. Kwic otherwise.
        // But to show words input this value has to be defined for navigation.
        maximum: 5
      },
    }
  },
  pagination: {
    pageSizes: [50, 100, 500, 1000],
  },
  download: {
    kwic: [
      // Do not allow excel download by now. It probably require specific changes for this project.
      {name: 'page_download', type: 'Page', mimeType: 'text/csv', filename: 'result.csv', pageViewName: 'download'},
    ],
  }
}
